"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import GetInTouchHeader from "@/components/get-in-touch/GetInTouchHeader";
import { workTypesToLabel } from "@/components/get-in-touch/_constants/workTypes";
import ClientDetailsCard from "./ClientDetailsCard";
import ProjectDetailsCard from "./ProjectDetailsCard";
import { useEnquiryFlowStore } from "@/store/enquiryFlowStore";
import { navigateBack } from "@/components/get-in-touch/enquiryFlowHistory";
import { isSurveyCompleted } from "@/lib/utils/survey-storage";
import { useToastContext } from "@/providers/toastProvider";
import { createLeadGenV2 } from "@/lib/utils/lead-gen-v2-api";
import { buildLeadGenPayload } from "@/lib/utils/lead-gen-payload";
import { addConnectedProfile, isProfileConnected } from "@/lib/utils/connected-profiles-storage";
import { saveLastEnquiry } from "@/lib/utils/last-enquiry-storage";
import { pushGtmEvent } from "@/lib/utils/gtmEventHandler";
import { inhomzEvents } from "@/app/(fevicol-inhomz)/_lib/events";
import { profileEventProps } from "@/app/(fevicol-inhomz)/_lib/profileEventProps";
import { useInhomzTrack } from "@/app/(fevicol-inhomz)/_hooks/useInhomzTrack";
import { useTrackWhenActive } from "@/app/(fevicol-inhomz)/_hooks/useTrackWhenActive";
export default function ReviewSheet() {
  const $ = _c(49);
  const {
    activeStep,
    digitalProfile,
    mandatoryData,
    surveyAnswers,
    goToSuccess
  } = useEnquiryFlowStore();
  const track = useInhomzTrack();
  let t0;
  if ($[0] !== digitalProfile) {
    t0 = profileEventProps(digitalProfile);
    $[0] = digitalProfile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useTrackWhenActive(activeStep === "review", inhomzEvents.reviewForm.VIEWED, t0);
  const {
    showSnackbar
  } = useToastContext();
  let t1;
  if ($[2] !== mandatoryData) {
    t1 = mandatoryData ? {
      scopeOfWork: workTypesToLabel(mandatoryData.workTypes),
      fullName: mandatoryData.fullName,
      contactNumber: mandatoryData.contactNumber,
      siteLocation: mandatoryData.siteLocation,
      flatNumber: mandatoryData.flatNumber
    } : {};
    $[2] = mandatoryData;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const clientDetails = t1;
  let t2;
  if ($[4] !== digitalProfile || $[5] !== track) {
    t2 = () => {
      track(inhomzEvents.reviewForm.BACK_CLICKED, profileEventProps(digitalProfile));
      navigateBack();
    };
    $[4] = digitalProfile;
    $[5] = track;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleBack = t2;
  let t3;
  if ($[7] !== digitalProfile || $[8] !== mandatoryData || $[9] !== surveyAnswers) {
    t3 = async () => {
      const payload = buildLeadGenPayload({
        digitalProfile,
        mandatoryData,
        surveyAnswers
      });
      return createLeadGenV2(payload, digitalProfile?.contractorId);
    };
    $[7] = digitalProfile;
    $[8] = mandatoryData;
    $[9] = surveyAnswers;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== digitalProfile || $[12] !== goToSuccess || $[13] !== mandatoryData || $[14] !== track) {
    t4 = () => {
      const alreadyConnected = isProfileConnected(digitalProfile?.contractorId);
      addConnectedProfile(digitalProfile?.contractorId, digitalProfile?.profileName);
      saveLastEnquiry(mandatoryData);
      pushGtmEvent({
        event_name: alreadyConnected ? "enquire_again_form_action_taken" : "enquiry_submitted",
        ...(alreadyConnected ? {
          event_action: "prefilled_form_submitted"
        } : {}),
        homeowner_name: mandatoryData?.fullName,
        homeowner_contact_number: mandatoryData?.contactNumber,
        homeowner_site_address: mandatoryData?.siteLocation,
        contractor_name: digitalProfile?.profileName,
        contractor_id: digitalProfile?.contractorId,
        contractor_phone_number: digitalProfile?.phoneNumber
      });
      track(inhomzEvents.enquirySubmitted, {
        ...profileEventProps(digitalProfile),
        already_connected: alreadyConnected,
        work_types: mandatoryData?.workTypes?.join(", ")
      });
      goToSuccess();
    };
    $[11] = digitalProfile;
    $[12] = goToSuccess;
    $[13] = mandatoryData;
    $[14] = track;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== showSnackbar) {
    t5 = error => {
      showSnackbar(error?.message || "Failed to submit enquiry. Please try again.", "error");
    };
    $[16] = showSnackbar;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const submitMutation = useMutation(t6);
  let t7;
  if ($[22] !== handleBack) {
    t7 = <GetInTouchHeader title="Review" onBack={handleBack} bgVar="--colorSurfaceSurfaceVar" />;
    $[22] = handleBack;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== clientDetails) {
    t8 = <ClientDetailsCard data={clientDetails} />;
    $[24] = clientDetails;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== surveyAnswers) {
    t9 = <ProjectDetailsCard answers={surveyAnswers} />;
    $[26] = surveyAnswers;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="flex-1 space-y-6 overflow-y-auto p-4">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== digitalProfile || $[32] !== submitMutation || $[33] !== track) {
    t11 = () => {
      track(inhomzEvents.reviewForm.SUBMIT_CLICKED, {
        ...profileEventProps(digitalProfile),
        already_connected: isProfileConnected(digitalProfile?.contractorId)
      });
      submitMutation.mutate();
    };
    $[31] = digitalProfile;
    $[32] = submitMutation;
    $[33] = track;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== submitMutation.isPending || $[36] !== surveyAnswers) {
    t12 = submitMutation.isPending || !isSurveyCompleted(surveyAnswers);
    $[35] = submitMutation.isPending;
    $[36] = surveyAnswers;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== submitMutation.isPending) {
    t13 = submitMutation.isPending && <div className="h-4 w-4 animate-spin rounded-full border-2 border-[--textPrimaryOnColorPrimary] border-t-transparent" />;
    $[38] = submitMutation.isPending;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const t14 = submitMutation.isPending ? "Sending..." : "Send enquiry";
  let t15;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14) {
    t15 = <div className="p-4 sticky bottom-0 bg-[--colorSurfaceSurface]"><button type="button" onClick={t11} disabled={t12} className="m3-label-large flex h-12 w-full items-center justify-center gap-2 rounded-[--radius100] bg-[--colorPrimaryPrimary] text-[--textPrimaryOnColorPrimary] disabled:opacity-60 disabled:cursor-wait">{t13}{t14}</button></div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t10 || $[46] !== t15 || $[47] !== t7) {
    t16 = <div className="mx-auto flex h-full w-full flex-col bg-[--colorSurfaceSurfaceVar]">{t7}{t10}{t15}</div>;
    $[45] = t10;
    $[46] = t15;
    $[47] = t7;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
}